import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { useAvailableFunnelRoute } from '~/app/hooks/useAvailableFunnelRoute'
import EyeClosed from '~/assets/images/closed-eye.svg'
import EyeOpened from '~/assets/images/opened-eye.svg'
import { Input } from '~/shared/components/Input'
import { Modal } from '~/shared/components/Modal'
import { PrivacyPolicyLink } from '~/shared/components/PrivacyPolicyLink'
import { Spinner } from '~/shared/components/Spinner'
import { TermsOfUseLink } from '~/shared/components/TermsOfUseLink'
import { Cohort } from '~/shared/constants/commonConstants'
import { PageId } from '~/shared/constants/pagesConstants'
import { COLORS } from '~/shared/constants/uiConstants'
import { getPathFromPageId } from '~/shared/helpers/getPathFromPageId'
import { useFirebaseAuth } from '~/shared/hooks/useFirebaseAuth'
import { firebaseCreateAccount } from '~/shared/lib/firebase'
import { eventLogger } from '~/shared/services/eventLogger'
import { resetErrorAction } from '~/shared/store/common/actions'
import {
  selectCohortID,
  selectError,
  selectIsFetching,
} from '~/shared/store/common/selects'
import { bindUserAction } from '~/shared/store/user/actions'
import {
  selectUserEmail,
  selectUserStatus,
  selectUserUUID,
} from '~/shared/store/user/selects'
import { appHistory } from '~/shared/utils'

import { Styles as S } from './styles'

const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  'auth/email-already-in-use': 'commonComponents.haveAccountError',
  'auth/weak-password': 'commonComponents.weakPassword',
}

const Login = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const isFetching = useSelector(selectIsFetching)
  const error = useSelector(selectError)
  const userStatus = useSelector(selectUserStatus)
  const uuid = useSelector(selectUserUUID)
  const cohort = useSelector(selectCohortID)
  const onboardingEmail = useSelector(selectUserEmail)
  const { search } = useLocation()

  const availableRoute = useAvailableFunnelRoute()

  const isGeneral = cohort === Cohort.GENERAL

  useLayoutEffect(() => {
    if (!isGeneral) return undefined

    document.documentElement.style.setProperty('--header-bg', COLORS.WHITE)

    return () => {
      document.documentElement.style.removeProperty('--header-bg')
    }
  }, [isGeneral])

  const [email, setEmail] = useState(onboardingEmail)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emailTouched, setEmailTouched] = useState(false)

  const isEmailValid = !emailTouched || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const isPasswordValid = password.length === 0 || password.length >= 8

  const isDisabled = isSubmitting || isFetching

  const handleAuth = useCallback(
    ({
      token,
      email: firebaseEmail,
    }: {
      token: string
      email: string | null
    }) => {
      dispatch(bindUserAction(token, firebaseEmail ?? email))
    },
    [dispatch, email],
  )

  useFirebaseAuth({ onAuth: handleAuth })

  useEffect(() => {
    eventLogger.logCreateAccountPageShown()
  }, [])

  useEffect(() => {
    if (!userStatus?.account.hasAccount) return

    // `useUpsell.ts` tags the redirect here with `fromUpsell=1` whenever upsell
    // was already resolved (skipped or purchased) before login — skipping never
    // updates userStatus.upsell server-side, so that state can't be inferred
    // from userStatus/availableRoute alone. Without that tag, this is the
    // login-before-upsell test variant reaching login first, so send the user
    // on to the still-pending upsell page (via availableRoute); otherwise go
    // straight to download.
    const cameFromUpsell = new URLSearchParams(search).get('fromUpsell') === '1'
    const nextPageId =
      !cameFromUpsell && availableRoute && availableRoute !== PageId.LOGIN
        ? availableRoute
        : PageId.DOWNLOAD

    const nextPath = getPathFromPageId({
      pageId: nextPageId,
      cohort,
      uuid,
      currentSearch: search,
    })

    appHistory.replace(nextPath)
  }, [userStatus?.account.hasAccount, availableRoute, cohort, uuid, search])

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setIsSubmitting(true)

      try {
        await firebaseCreateAccount(email, password)
      } catch (err: any) {
        const i18nKey =
          FIREBASE_ERROR_MESSAGES[err?.code] ?? 'commonComponents.commonError'
        eventLogger.logAccountCreationFailed({
          email,
          errorReason: err?.code ?? 'unknown',
        })
        eventLogger.logAccountCreated(email)
        if (err?.code === 'auth/email-already-in-use') {
          setEmailError(t(i18nKey))
        } else {
          setModalError(t(i18nKey))
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [email, password, t],
  )

  const closeModal = useCallback(() => {
    setModalError(null)
    dispatch(resetErrorAction())
  }, [dispatch])

  return (
    <>
      <S.StylesContainer>
        {isDisabled && (
          <S.LoaderOverlay>
            <Spinner isFullScreen={false} />
          </S.LoaderOverlay>
        )}

        <S.Title>
          <Trans i18nKey="login.title" components={{ b: <b /> }} />
        </S.Title>

        <form onSubmit={handleSubmit}>
          <Input
            name="email"
            placeholder={t('login.emailPlaceholder')}
            type="email"
            value={email}
            disabled={isDisabled}
            isValid={isEmailValid && !emailError}
            validationText={emailError ?? t('login.invalidEmail')}
            onChange={(e) => {
              setEmail(e.target.value)
              setEmailError(null)
            }}
            onBlur={() => setEmailTouched(true)}
            marginBottom={16}
          />

          <S.PasswordWrapper>
            <Input
              name="password"
              placeholder={t('login.passwordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              disabled={isDisabled}
              isValid={isPasswordValid}
              validationText={t('login.passwordMinLength')}
              onChange={(e) => setPassword(e.target.value)}
              marginBottom={0}
            />
            <S.PasswordToggle
              type="button"
              disabled={isDisabled}
              onClick={() => setShowPassword((v) => !v)}
            >
              <img
                src={showPassword ? EyeOpened : EyeClosed}
                alt={showPassword ? 'Show Password' : 'Hide Password'}
              />
            </S.PasswordToggle>
          </S.PasswordWrapper>

          <S.Disclaimer>
            <Trans
              i18nKey="login.disclaimer"
              components={{
                privacyPolicy: <PrivacyPolicyLink />,
                termsOfUse: <TermsOfUseLink />,
              }}
            />
          </S.Disclaimer>

          <S.SubmitButton
            type="submit"
            disabled={
              !email ||
              !password ||
              !isEmailValid ||
              !isPasswordValid ||
              isDisabled
            }
          >
            {t('login.submitButton')}
          </S.SubmitButton>
        </form>
      </S.StylesContainer>
      <Modal isShown={!!(modalError || error)} onClose={closeModal}>
        {modalError || error}
      </Modal>
    </>
  )
}

export default Login
